import React, { useState } from 'react';
import { Box, Tabs, Tab, Snackbar, Alert, useTheme } from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import KeyIcon from '@mui/icons-material/Key';
import Button from '@mui/material/Button';
import ModalChaveSeguranca from '../ui/ModalChaveSeguranca';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useLogin } from '../../contexts/LoginContext';
import { urlBase } from '../../api';
import ListaTarefasBackup from './ListaTarefasBackup';
import ArquivosDaTarefa from './ArquivosDaTarefa';
import PesquisaArquivos from './PesquisaArquivos';
import VisualizadorLogs from './VisualizadorLogs';
import { CentralBackupsApi } from './services/centralBackupsApi';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`central-backups-tabpanel-${index}`}
      aria-labelledby={`central-backups-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const CentralBackups: React.FC = () => {
  const [login] = useLogin();
  const token = login.accessToken;
  const clienteID = login.usuario?.usuarioCliente?.clienteID || '';
  const theme = useTheme();

  const [abaAtiva, setAbaAtiva] = useState(0);

  // Controle de sub-visualização para a aba de tarefas (lista geral vs. arquivos da tarefa)
  const [subAbaTarefas, setSubAbaTarefas] = useState<{
    tipo: 'lista' | 'arquivos_tarefa';
    tarefaID?: string;
    nomeTarefa?: string;
  }>({ tipo: 'lista' });

  // Pré-filtro de tarefa inicial para os logs
  const [logTarefaInicialID, setLogTarefaInicialID] = useState<string | null>(null);

  // Estado de download de arquivos
  const [baixandoArquivoID, setBaixandoArquivoID] = useState<string | null>(null);
  const [snackbarDownloadMsg, setSnackbarDownloadMsg] = useState('');
  const [modalChaveAberto, setModalChaveAberto] = useState(false);
  const [snackbarDownloadAberto, setSnackbarDownloadAberto] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const lidarComMudarAba = (event: React.SyntheticEvent, novaAba: number) => {
    setAbaAtiva(novaAba);
  };

  const lidarComVerArquivosDaTarefa = (tarefaAgendadaID: string, nomeTarefa: string) => {
    setSubAbaTarefas({
      tipo: 'arquivos_tarefa',
      tarefaID: tarefaAgendadaID,
      nomeTarefa
    });
    // Garante que estamos na aba 0 (Tarefas)
    setAbaAtiva(0);
  };

  const lidarComVerLogsDaTarefa = (tarefaAgendadaID: string, nomeTarefa: string) => {
    setLogTarefaInicialID(tarefaAgendadaID);
    // Alterna para a aba 2 (Logs)
    setAbaAtiva(2);
  };

  const lidarComDownloadArquivo = (arquivoID: string, nomeArquivo: string) => {
    setBaixandoArquivoID(arquivoID);
    CentralBackupsApi.solicitarArquivoDownload(arquivoID, token)
      .then((tokenTemporario) => {
        const link = document.createElement('a');
        link.href = `${urlBase}/arquivo/baixar-arquivo/${tokenTemporario}`;
        link.target = '_blank';
        link.download = nomeArquivo;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setSnackbarDownloadMsg(`Download de ${nomeArquivo} iniciado!`);
        setSnackbarSeverity('success');
        setSnackbarDownloadAberto(true);
        setBaixandoArquivoID(null);
      })
      .catch((err) => {
        console.error("Erro ao solicitar download do arquivo", err);
        const mensagemServidor = err?.response?.data?.mensagem;
        setSnackbarDownloadMsg(mensagemServidor || `Falha ao baixar o arquivo: ${nomeArquivo}`);
        setSnackbarSeverity('error');
        setSnackbarDownloadAberto(true);
        setBaixandoArquivoID(null);
      });
  };

  if (!clienteID) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Acesso inválido: ID do cliente não localizado na sessão atual. Por favor, faça login novamente.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Barra de Abas e Chave de Segurança */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${theme.palette.divider}` }}>
      <Tabs
        value={abaAtiva}
        onChange={lidarComMudarAba}
        textColor="primary"
        indicatorColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Abas da Central de Backups"
        sx={{
          '.MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.95rem',
            color: theme.palette.text.secondary,
            minHeight: '48px',
            gap: 1,
            '&.Mui-selected': {
              color: theme.palette.mode === 'dark' ? '#38BDF8' : theme.palette.primary.main
            }
          },
          '.MuiTabs-indicator': {
            backgroundColor: theme.palette.mode === 'dark' ? '#38BDF8' : theme.palette.primary.main,
            height: '3px'
          }
        }}
      >
        <Tab icon={<StorageIcon fontSize="small" />} iconPosition="start" label="Tarefas de Backup" id="central-backups-tab-0" />
        <Tab icon={<SearchIcon fontSize="small" />} iconPosition="start" label="Pesquisa de Arquivos" id="central-backups-tab-1" />
        <Tab icon={<ReceiptLongIcon fontSize="small" />} iconPosition="start" label="Logs de Execução" id="central-backups-tab-2" />
      </Tabs>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<KeyIcon />}
          onClick={() => setModalChaveAberto(true)}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            mr: 1,
            whiteSpace: 'nowrap'
          }}
        >
          Chave de Segurança
        </Button>
      </Box>

      {/* Painel da Aba 0: Tarefas */}
      <TabPanel value={abaAtiva} index={0}>
        {subAbaTarefas.tipo === 'lista' ? (
          <ListaTarefasBackup
            clienteID={clienteID}
            token={token}
            onVerArquivos={lidarComVerArquivosDaTarefa}
            onVerLogs={lidarComVerLogsDaTarefa}
            onBaixarUltimo={lidarComDownloadArquivo}
            baixandoArquivoID={baixandoArquivoID}
          />
        ) : (
          <ArquivosDaTarefa
            tarefaAgendadaID={subAbaTarefas.tarefaID!}
            nomeTarefa={subAbaTarefas.nomeTarefa!}
            token={token}
            onVoltar={() => setSubAbaTarefas({ tipo: 'lista' })}
            onBaixar={lidarComDownloadArquivo}
            baixandoArquivoID={baixandoArquivoID}
          />
        )}
      </TabPanel>

      {/* Painel da Aba 1: Pesquisa Avançada */}
      <TabPanel value={abaAtiva} index={1}>
        <PesquisaArquivos
          clienteID={clienteID}
          token={token}
          onBaixar={lidarComDownloadArquivo}
          baixandoArquivoID={baixandoArquivoID}
        />
      </TabPanel>

      {/* Painel da Aba 2: Logs */}
      <TabPanel value={abaAtiva} index={2}>
        <VisualizadorLogs
          clienteID={clienteID}
          token={token}
          tarefaAgendadaIDInicial={logTarefaInicialID}
          onLimparTarefaAgendadaIDInicial={() => setLogTarefaInicialID(null)}
        />
      </TabPanel>

      {/* Snackbar Geral de Download */}
      <Snackbar
        open={snackbarDownloadAberto}
        autoHideDuration={4000}
        onClose={() => setSnackbarDownloadAberto(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarDownloadAberto(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            fontWeight: 600,
            borderRadius: '8px',
            backgroundColor: snackbarSeverity === 'success' ? '#10B981' : '#EF4444',
            color: '#FFFFFF'
          }}
        >
          {snackbarDownloadMsg}
        </Alert>
      </Snackbar>

      {/* Modal de Recuperação de Chave de Segurança */}
      <ModalChaveSeguranca
        open={modalChaveAberto}
        onClose={() => setModalChaveAberto(false)}
        clienteID={clienteID}
      />
    </Box>
  );
};

export default CentralBackups;
